// src/features/calendar/FindTimePanel.tsx
// "Find a time" — suggests slots when all attendees are free (FindAvailableSlots)
// and shows each attendee's busy intervals for the search day (GetFreeBusy).

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, Alert } from '@react/components';
import { useScope } from '@/hooks/useScope';
import { FindAvailableSlots } from '@core/api/endpoints/calendar/find_available_slots';
import { GetFreeBusy } from '@core/api/endpoints/calendar/get_free_busy';
import type { Actor } from '@core/types/actor';
import type { AvailableSlot, ActorFreeBusy } from '@core/types/calendar';
import { makeEntity, fmtDateTime, fmtTime, errMessage, toDateInputValue, addDays, startOfDay, minutesToDuration } from './time';

interface FindTimePanelProps {
  attendees: Actor[];
  /** Meeting length in minutes. */
  durationMinutes: number;
  onPick: (start: string, end: string) => void;
}

export const FindTimePanel = observer(({ attendees, durationMinutes, onPick }: FindTimePanelProps) => {
  const { t, locale } = useI18n();
  const scope = useScope();

  const [searchDate, setSearchDate] = useState(toDateInputValue(new Date()));
  const [respectWorkingHours, setRespectWorkingHours] = useState(true);
  const [slots, setSlots] = useState<AvailableSlot[] | null>(null);
  const [freeBusy, setFreeBusy] = useState<ActorFreeBusy[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async () => {
    setIsSearching(true);
    setError(null);
    try {
      const from = startOfDay(new Date(searchDate));
      const to = addDays(from, 7);
      const scopeEntity = makeEntity(scope.productId, 'product', scope);
      const [slotsRes, fbRes] = await Promise.all([
        FindAvailableSlots({
          scope: scopeEntity,
          attendees,
          duration: minutesToDuration(durationMinutes),
          search_start: from.toISOString(),
          search_end: to.toISOString(),
          respect_working_hours: respectWorkingHours,
          max_results: 8,
        }),
        attendees.length > 0
          ? GetFreeBusy({
              scope: scopeEntity,
              actors: attendees,
              time_min: from.toISOString(),
              time_max: addDays(from, 1).toISOString(),
            })
          : Promise.resolve(null),
      ]);
      setSlots(slotsRes.slots?.slots ?? []);
      setFreeBusy(fbRes ? Object.values(fbRes.actor_freebusy ?? {}) : []);
    } catch (e) {
      setError(errMessage(e, t('calendar.findTime.error')));
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-sm font-medium text-foreground">
          {t('calendar.findTime.searchFrom')}
          <input
            type="date"
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground"
          />
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={respectWorkingHours}
            onChange={(e) => setRespectWorkingHours(e.target.checked)}
          />
          {t('calendar.findTime.respectWorkingHours')}
        </label>
        <Button type="button" size="sm" variant="secondary" onClick={search} isLoading={isSearching}>
          {t('calendar.findTime.search')}
        </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {slots !== null && !isSearching && (
        <div className="space-y-1.5">
          {slots.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('calendar.findTime.noSlots')}</p>
          )}
          {slots.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => s.start_time && s.end_time && onPick(s.start_time, s.end_time)}
              className="flex w-full items-center justify-between rounded-md border border-input px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted"
            >
              <span>{fmtDateTime(s.start_time, locale)} – {fmtTime(s.end_time, locale)}</span>
              <span className="text-xs text-muted-foreground">
                {t('calendar.findTime.freeCount', {
                  free: s.available_attendee_count,
                  total: s.total_attendee_count,
                })}
              </span>
            </button>
          ))}
        </div>
      )}

      {freeBusy.length > 0 && (
        <div className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">{t('calendar.findTime.busyOn')}</span>
          {freeBusy.map((fb, i) => (
            <div key={i} className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{fb.actor?.display_name ?? '—'}</span>{' '}
              {(fb.slots?.slots ?? []).length === 0
                ? t('calendar.findTime.free')
                : (fb.slots?.slots ?? [])
                    .map((s) => `${fmtTime(s.start_time, locale)}–${fmtTime(s.end_time, locale)}`)
                    .join(', ')}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});
